import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  Typography,
  Paper,
} from "@mui/material";
import {
  useGetDuplicateJobOrdersQuery,
  useMarkDuplicatesDeletedMutation,
} from "../../redux/api/jobOrder";
import { useEffect, useState } from "react";
import {
  JobOrderDuplicate,
  JobRating,
  JobRatingLabel,
} from "../../common/JobOrder";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import { formatDateString } from "../../common/FormatDateString";
import useDocumentTitle from "../../util/useDocumentTitle";

type DuplicateComponentType = JobOrderDuplicate & { checked: boolean };

const DuplicateJobOrders = () => {
  useDocumentTitle("Duplicate Job Orders");
  const { data, isLoading, refetch } = useGetDuplicateJobOrdersQuery({
    refetchOnMountOrArgChange: true,
  });
  const [jobOrders, setJobOrders] = useState<DuplicateComponentType[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteDuplicates] = useMarkDuplicatesDeletedMutation();

  useEffect(() => {
    if (!!data) {
      setJobOrders(data.map((jo) => ({ ...jo, checked: false })));
    } else {
      setJobOrders([]);
    }
  }, [data]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  const deleteSelected = async () => {
    const response = await deleteDuplicates({
      jobOrderIds: jobOrders.filter((jo) => jo.checked).map((jo) => jo.id),
    });
    if ("error" in response === false) {
      refetch();
      toast.success(<p>Duplicates successfully marked as Deleted</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      handleClose();
    }
  };

  const handleSelectAll = () => {
    const temp = [...jobOrders];
    temp.forEach((jo) => (jo.checked = true));
    setJobOrders(temp);
  };

  const handleDeselectAll = () => {
    const temp = [...jobOrders];
    temp.forEach((jo) => (jo.checked = false));
    setJobOrders(temp);
  };

  const handleChange = (e: any, idx: number) => {
    const temp = [...jobOrders];
    temp[idx].checked = e.target.checked;
    setJobOrders(temp);
  };

  const handleOpen = () => {
    setConfirmDelete(true);
  };

  const handleClose = () => {
    setConfirmDelete(false);
  };

  return (
    <>
      <h1>Duplicate Job Orders</h1>
      <div className="my-1">
        <Button onClick={handleSelectAll}>Select All</Button>
        <Button onClick={handleDeselectAll}>De-Select All</Button>
        <Button onClick={handleOpen}>Mark Selected as Deleted</Button>
      </div>

      <div className="flex-col g-2 wd-100">
        {jobOrders?.map((jo, i) => (
          <Paper className="p-2" key={jo.id}>
            <li className="flex align-items-center">
              <Checkbox
                checked={jo.checked}
                onChange={(e) => handleChange(e, i)}
              />
              <h3 className="flex g-0 align-items-center ft-18">
                Job Order ID:
                <NavLink
                  className="blue-link table-link ml-0"
                  to={`/jobOrders/${jo.id}`}
                >
                  {jo.id}
                </NavLink>
              </h3>
            </li>
            <div className="flex ml-1 duplicate-container">
              <div className="flex align-items-center">
                <b className="b-600">Crew Change Needed:</b>
                <div className="border-right">
                  {" "}
                  {formatDateString(jo.fillByDate)}
                </div>
              </div>
              <div className="flex align-items-center">
                <b className="pl-2 b-600"> Port:</b>{" "}
                <div className="border-right">{jo.port} </div>
              </div>
              <div className="flex align-items-center">
                <b className="pl-2 b-600">Vessel Name:</b>{" "}
                <div className="border-right">{jo.vesselName}</div>
              </div>
              <div className="flex align-items-center">
                <b className="pl-2 b-600">Rating: </b>
                <div className="border-right">
                  {
                    JobRatingLabel[
                      JobRating[jo.rating as keyof typeof JobRating]
                    ]
                  }
                </div>
              </div>
              <div className="flex align-items-center">
                <b className="pl-2 b-600"> Offsigner:</b>
                <div className="border-right">
                  {" "}
                  {!jo.offsignerId ? (
                    ""
                  ) : (
                    <NavLink
                      className="blue-link table-link b-500"
                      to={`/profiles/${jo.offsignerId}`}
                    >
                      {[jo.offsignerFirstName, jo.offsignerLastName].join(" ")}
                    </NavLink>
                  )}{" "}
                </div>
              </div>
              <div className="flex align-items-center">
                <h4 className="pl-2">Onsigner:</h4>
                <div className="py-0 pr-2 pl-1">
                  {!jo.onsignerId ? (
                    ""
                  ) : (
                    <NavLink
                      className="blue-link table-link b-500"
                      to={`/profiles/${jo.onsignerId}`}
                    >
                      {[jo.onsignerFirstName, jo.onsignerLastName].join(" ")}
                    </NavLink>
                  )}
                </div>
              </div>
            </div>
          </Paper>
        ))}
      </div>
      <Dialog
        open={confirmDelete}
        onClose={handleClose}
        aria-labelledby="modal-title"
      >
        <Typography variant="h3" id="modal-title">
          Mark Job Orders as Deleted
        </Typography>
        <p className="mt-1">
          Are you sure you would like to mark the selected Job Orders as
          deleted?
        </p>
        <div className="modal-footer">
          <Button className="outline-button-small" onClick={handleClose}>
            Cancel
          </Button>
          <Button className="blue-button-small" onClick={deleteSelected}>
            Confirm
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default DuplicateJobOrders;
